/* eslint-disable */
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MessageComposer from 'components/MessageComposer';
import { JobChatMessageThread } from '@icimscorp/mui-acs';
import { Box } from '@material-ui/core';
import {
  sendMessage,
  getMessages,
  realTimeMsg,
  sendAnonMessage,
  enterChat,
  uploadResumeReq,
} from 'redux/actions';
import { Realtime } from 'services/websocket';
import { Api } from 'services/api';
import { AppMessenger, ariConfig } from 'utils';
import { getDemoMessages } from 'chatDemoMode';
import { cacheKey } from 'appConstants';
import { resumeUploadJobSearchPendo } from './helper';
import { SET_OR_UPDATE_BADGE } from 'widgetActions';
import { useInitializePendo } from './useInitializePendo';
import { cache } from 'utils/cache';

const sendMsg = ({ text, isAnonymous, dispatch, jobChatId, firstName, lastName, phoneNumber }) => {
  const msg = text.trim();
  if (msg) {
    if (isAnonymous) {
      dispatch(sendAnonMessage({ text: msg, jobChatId }));
    } else {
      dispatch(sendMessage({ firstName, lastName, phoneNumber, text: msg, jobChatId }));
    }
  }
};

let retryCount = 0;

const ChatView = () => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const jobChatId = cache.get(cacheKey.JOB_CHAT_ID);
  const {
    candidate,
    messages,
    sequenceId,
    isLoadingMsgs,
    isAnonymous,
    isChatWidgetOpen,
    unreadMsgsCount,
    isInChat,
    isSendingMsg,
    resumeUploaded,
  } = useSelector((state) => state.appReducer);
  const { firstName, lastName, phone: phoneNumber } = candidate;
  const [msgs, setMsgs] = React.useState([...messages]);
  const [isAwaitingResponse, setIsAwaitingResponse] = React.useState(false);
  const intervalId = React.useRef(null);

  const getMsgs = () => {
    ++retryCount;
    if (msgs.length === 0 && retryCount < 5) {
      dispatch(getMessages({ jobChatId, candidateId: candidate.id }));
    } else {
      clearInterval(intervalId.current);
    }
  };

  const getRecentMessages = () => {
    if (msgs.length === 0 && retryCount < 5) {
      dispatch(getMessages({ jobChatId, candidateId: candidate.id }));
    }
  };

  const handleSubmitFeedback = ({ id, rating, message }) => {
    Api.novaFeedback({
      messageId: id,
      messageFeedback: {
        type: rating,
        comment: message,
      },
    });
  };

  // TODO: I have no clue what this logic is for. If not in chat enter chat?? -Amir
  if (!isInChat) {
    dispatch(enterChat());
  }

  useInitializePendo(candidate, jobChatId);

  React.useEffect(() => {
    if (messages.length) {
      setMsgs((prevMsgs) => prevMsgs.slice(0, 0).concat(messages));
      clearInterval(intervalId.current);
    }
  }, [messages]);

  React.useEffect(() => {
    intervalId.current = setInterval(getMsgs, 1000);
    return () => clearInterval(intervalId.current);
  }, []);

  React.useEffect(() => {
    if (getDemoMessages()) {
      const demoMsgs = getDemoMessages();
      setMsgs((prevMsgs) => prevMsgs.slice(0, 0).concat(demoMsgs));
    } else {
      Realtime.connect({
        onOpenCallback: getRecentMessages,
        onMessageCallback: (data) => {
          if (data.message) {
            dispatch(realTimeMsg(data.message));
          }
        },
      });
    }
  }, []);

  React.useEffect(() => {
    AppMessenger.sendMessageToWidget({
      action: SET_OR_UPDATE_BADGE,
      additionalData: { value: unreadMsgsCount },
    });
  }, [isChatWidgetOpen, messages.length]);

  React.useEffect(() => {
    const lastMsg = msgs[msgs.length - 1];
    if (resumeUploaded && lastMsg.type === 1) {
      setIsProcessing(false);
    }
    if (lastMsg) {
      setIsAwaitingResponse(lastMsg.type === 0);
    }
  }, [msgs]);

  const handleFileUpload = (file) => {
    dispatch(uploadResumeReq());
    setIsProcessing(true);
    Api.uploadCandidateResume({ file, candidateId: candidate.id, sequenceId })
      .then(() => resumeUploadJobSearchPendo(candidate, jobChatId))
      .catch((error) => {
        sumoLogger.log({
          error,
          additionalInfo: {
            message: 'Failed To Upload Resume From JobChat',
            candidateId: candidate.id,
            candidateName: candidate.firstName
              ? `${candidate.firstName} ${candidate.lastName}`
              : 'Anonymous Candidate',
            phoneNumber: candidate.phone ? candidate.phone : 'Anonymous Candidate',
            customerId: candidate.customerId,
            jobChatId,
          },
        });
        setIsProcessing(false);
      });
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <JobChatMessageThread
        messages={msgs}
        isLoading={isLoadingMsgs}
        ariCustomData={{
          displayName: ariConfig.displayName,
          imgUrl: ariConfig.imgUrl,
          imgAlt: ariConfig.imgAlt,
        }}
        fileUploadCallback={handleFileUpload}
        isProcessing={isProcessing}
        isAwaitingResponse={isAwaitingResponse}
        submitFeedback={handleSubmitFeedback}
      />
      <MessageComposer
        isSendingMsg={isSendingMsg}
        handleSend={({ text }) =>
          sendMsg({ text, isAnonymous, dispatch, jobChatId, firstName, lastName, phoneNumber })
        }
      />
    </Box>
  );
};

export default ChatView;
